import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, Trophy, Flame, Crown, Medal } from "lucide-react";
import SeoCopy from "@/components/SeoCopy";

export default function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/leaderboard").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="leaderboard-title">
        <Trophy strokeWidth={2.5} className="text-amber-500"/> Top Dashers
      </h1>
      <p className="text-slate-600 mb-4">Top point-getters in the community. Hit 5,000 to win a FREE bin cleaning. 🏆</p>

      {/* Podium */}
      {rows.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Podium rank={2} row={rows[1]} icon={<Medal className="text-slate-500"/>} />
          <Podium rank={1} row={rows[0]} icon={<Crown className="text-amber-500"/>} top/>
          <Podium rank={3} row={rows[2]} icon={<Medal className="text-orange-400"/>} />
        </div>
      )}

      <div className="card-clay overflow-hidden">
        <ul className="divide-y divide-slate-100" data-testid="leaderboard-list">
          {rows.map((r, i) => (
            <li key={i} className={`flex items-center gap-3 p-4 ${r.is_me ? "bg-amber-50" : ""}`} data-testid={`lb-row-${i}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-fredoka font-bold ${i === 0 ? "bg-amber-100 text-amber-800" : i < 3 ? "bg-slate-100 text-slate-700" : "bg-slate-50 text-slate-500"}`}>
                {i + 1}
              </div>
              <div className="flex-1">
                <div className="font-fredoka font-bold text-slate-900">{r.display_name} {r.is_me && <span className="text-xs font-normal text-amber-700">(you)</span>}</div>
                <div className="text-xs text-slate-500">{r.referral_count} referral(s) · {r.badges?.length || 0} badge(s)</div>
              </div>
              <div className="text-right">
                <div className="font-fredoka text-xl font-bold text-slate-900">{(r.dash_points || 0).toLocaleString()}</div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">points</div>
              </div>
            </li>
          ))}
          {rows.length === 0 && <li className="p-8 text-center text-slate-400">No players yet. Be the first!</li>}
        </ul>
      </div>
      <SeoCopy
        title="How Dash Rewards work"
        links={[
          { to: "/schedule", label: "Schedule a pickup" },
          { to: "/reviews", label: "Leave a review" },
          { to: "/policies", label: "Policies & FAQ" },
          { to: "/about", label: "Meet the Kids" },
        ]}
      >
        <p>The <strong>Dash Rewards</strong> leaderboard ranks Bag Dash Kids customers by total points earned. You stack points by paying for pickups (50 pts each), keeping a weekly subscription active (25 pts bonus), referring neighbors who book a paid pickup (100 pts), and writing reviews (50 pts). Five paid weeks in a row earns a 250-point streak bonus.</p>
        <p>At 4 paid pickups you earn your first reward — a free 1-bag pickup, a discounted bin cleaning, or a priority pickup slot. Points never expire, and Mom Manager Donja can issue bonus points for community helpers.</p>
      </SeoCopy>
    </div>
  );
}

const Podium = ({ rank, row, icon, top }) => (
  <div className={`card-clay p-4 text-center ${top ? "bg-amber-50 border-amber-200 transform sm:-translate-y-2" : ""}`}>
    <div className="flex justify-center">{icon}</div>
    <div className="font-fredoka font-bold mt-2 truncate">{row?.display_name || "—"}</div>
    <div className="font-fredoka text-2xl font-bold text-slate-900">{(row?.dash_points || 0).toLocaleString()}</div>
    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">#{rank}</div>
  </div>
);
